import React, { useState, useEffect } from "react";
import { Box, RadioCardGroup, Text } from "@telia/teddy";
import format from "date-fns/format";
import _isEmpty from "lodash/isEmpty";
import { useDispatch, useSelector } from "react-redux";
import { HolidayData, RootState } from "../../../../../../models";
import { getDefaultPortinDate, getEarliestPossiblePortingDate } from "../../../../../../shared/util";
import { Alert } from "../../../../components";
import { getHolidayData, updatePortinDate } from "../../../../checkout/state/actions/addMemberActions";
import { getSelectedPhoneNumber } from "../../../../checkout/state/selectors/phoneNumberSelectors";
import {
    getSelectedPortinDate,
    getSubscription,
    getSubscriptions,
} from "../../../../checkout/state/selectors/subscriptionsSelectors";
import PortingCard from "../../../../update-porting-date/components/molecules/PortingCard/PortingCard";
import { nextSubscriptionStep } from "../../../state/actions/subscriptionStepsActions";
import { isEsimSelected } from "../../../state/selectors/customerInfoSelectors";
import type { SimDeliveryType, SimType } from "../../../views/Dashboard/simCardUtils";
import "./PortingDateCard.less";
import { SubscriptionStepsControls } from "../SubscriptionStepsControls";

type PortingDateSelection = "asap" | "custom";
const ASAP_DATE_UPDATED_MESSAGE = "Overføringstidspunktet er oppdatert. Se over tidspunktet og prøv igjen.";

interface Props {
    onBackButtonClick: () => void;
    selectedSim: SimType;
    selectedSimDelivery: SimDeliveryType;
    subscriptionId: string;
    description?: string;
}

const PortingDateCard: React.FC<Props> = ({
    subscriptionId,
    onBackButtonClick,
    selectedSim,
    selectedSimDelivery,
    description = " Du kan endre denne datoen senere. Du vil motta en SMS med mer informasjon om denne muligheten.",
}) => {
    const [holidayData, setHolidayData] = useState<HolidayData | undefined>(undefined);
    const dispatch = useDispatch();
    const isEsim = useSelector((state: RootState) => isEsimSelected(state));
    const hasEsimPortingRules = isEsim || (selectedSim === "sim" && selectedSimDelivery === "from_store");
    const portinDateFromBackend = useSelector(
        (state: RootState) => getSelectedPortinDate(state, subscriptionId) || getDefaultPortinDate(hasEsimPortingRules),
    );
    const subscription = useSelector((state: RootState) => getSubscription(state, subscriptionId));
    const allSubscriptions = useSelector((state: RootState) => getSubscriptions(state));
    const [selectedDate, setSelectedDate] = useState(new Date(portinDateFromBackend || ""));
    const productUserPhoneNumber = useSelector((state: RootState) => getSelectedPhoneNumber(state, subscriptionId));
    const [asapDate, setAsapDate] = useState(() => getEarliestPossiblePortingDate());
    const [portingDateSelection, setPortingDateSelection] = useState<PortingDateSelection>("asap");
    const [showAsapDateUpdatedMessage, setShowAsapDateUpdatedMessage] = useState(false);

    // Keep the estimate fresh in case the user stays on this page across a minute boundary
    useEffect(() => {
        const interval = setInterval(() => setAsapDate(getEarliestPossiblePortingDate()), 10_000);
        return () => clearInterval(interval);
    }, []);

    // Prefill the porting date selection from the subscription's stored selection type
    useEffect(() => {
        if (subscription?.portingDateSelectionType) {
            setPortingDateSelection(subscription.portingDateSelectionType);
        } else if (!subscription?.requestedPortingDate) {
            // Prefill with same selected date as other added sub
            const otherSubscriptionWithCustomDate = Object.values(allSubscriptions).find(
                (sub) =>
                    sub &&
                    sub.id !== subscriptionId &&
                    sub.portingDateSelectionType === "custom" &&
                    sub.requestedPortingDate,
            );

            if (otherSubscriptionWithCustomDate) {
                setPortingDateSelection("custom");
                setSelectedDate(new Date(otherSubscriptionWithCustomDate.requestedPortingDate as string));
            }
        }
    }, [subscription?.portingDateSelectionType, subscriptionId, portinDateFromBackend, allSubscriptions]);

    const handleOnContinue = () => {
        const latestAsapDate = getEarliestPossiblePortingDate();

        if (hasEsimPortingRules && portingDateSelection === "asap" && latestAsapDate.getTime() !== asapDate.getTime()) {
            setAsapDate(latestAsapDate);
            setShowAsapDateUpdatedMessage(true);
            return;
        }

        setShowAsapDateUpdatedMessage(false);
        const dateToUse = hasEsimPortingRules && portingDateSelection === "asap" ? latestAsapDate : selectedDate;
        const selectedDateISOString = dateToUse.toISOString();
        dispatch(
            updatePortinDate(
                selectedDateISOString,
                subscriptionId,
                hasEsimPortingRules ? portingDateSelection : "custom",
            ),
        );
        dispatch(nextSubscriptionStep(subscriptionId));
    };

    useEffect(() => {
        if (_isEmpty(holidayData)) {
            dispatch(getHolidayData()).then((data) => setHolidayData(data.holidayData));
        }
    }, []);

    const estimatedDateLabel = format(asapDate, "dd.MM.yyyy 'kl.' HH:mm");

    return (
        <div className="porting-date-card">
            {!isEsim && selectedSim === "sim" && selectedSimDelivery === "sent_home" && (
                <Text ml="100" mb="100" variant="additional-100">
                    SIM-kortet leveres i kundens postkasse 5–10 dager etter bestilling.
                </Text>
            )}
            {hasEsimPortingRules ? (
                <>
                    <RadioCardGroup
                        value={portingDateSelection}
                        onValueChange={(value) => {
                            setPortingDateSelection(value as PortingDateSelection);
                            setShowAsapDateUpdatedMessage(false);
                        }}
                        orientation="vertical"
                        name="porting-date-selection"
                    >
                        <RadioCardGroup.Item value="asap">
                            <RadioCardGroup.Title>Så fort som mulig</RadioCardGroup.Title>
                            <RadioCardGroup.Body>
                                Estimert: <span className="telia-sans-medium">{estimatedDateLabel}</span>
                            </RadioCardGroup.Body>
                        </RadioCardGroup.Item>
                        <RadioCardGroup.Item value="custom">
                            <RadioCardGroup.Title>Velg et tidspunkt</RadioCardGroup.Title>
                        </RadioCardGroup.Item>
                    </RadioCardGroup>
                    {showAsapDateUpdatedMessage && (
                        <Box mt="200">
                            <Alert kind="info">{ASAP_DATE_UPDATED_MESSAGE}</Alert>
                        </Box>
                    )}
                    {portingDateSelection === "custom" && (
                        <Box mt="200">
                            <PortingCard
                                selectedDate={selectedDate}
                                setSelectedDate={(date) => setSelectedDate(date)}
                                phoneNumber={productUserPhoneNumber}
                                isAllowedToUpdate={true}
                                holidayData={holidayData || {}}
                                shouldShowSameCheckBoxOnChanged={false}
                                shouldShowSameCheckBoxValue={false}
                                creationDate={new Date().toISOString()}
                                isEsim={hasEsimPortingRules}
                            />
                        </Box>
                    )}
                </>
            ) : (
                <PortingCard
                    selectedDate={selectedDate}
                    setSelectedDate={(date) => setSelectedDate(date)}
                    phoneNumber={productUserPhoneNumber}
                    isAllowedToUpdate={true}
                    holidayData={holidayData || {}}
                    shouldShowSameCheckBoxOnChanged={false}
                    shouldShowSameCheckBoxValue={false}
                    creationDate={new Date().toISOString()}
                    isEsim={hasEsimPortingRules}
                />
            )}
            {description && (
                <Text ml="100" variant="additional-100" mt="200">
                    {description}
                </Text>
            )}
            <div className="porting-date-card__button-container">
                <SubscriptionStepsControls onSecondaryClick={onBackButtonClick} onPrimaryClick={handleOnContinue} />
            </div>
        </div>
    );
};

export default PortingDateCard;
